import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

export default function Home() {
  const [experiences, setExperiences] = useState([])
  const [cities, setCities] = useState([])
  const [stats, setStats] = useState({ cities: 0, experiences: 0 })
  const [selectedExperience, setSelectedExperience] = useState(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [userLocation, setUserLocation] = useState(null)
  const mapContainer = useRef(null)
  const map = useRef(null)
  const markers = useRef([])

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('accessToken')
    setIsLoggedIn(!!token)

    // Fetch sample data for homepage
    fetchStats()
    fetchSampleExperiences()
  }, [])

  const fetchStats = async () => {
    try {
      const response = await fetch('https://locrawl-api.findrawdogfood.workers.dev/api/stats')
      const data = await response.json()
      setStats(data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const fetchSampleExperiences = async () => {
    try {
      const response = await fetch('https://locrawl-api.findrawdogfood.workers.dev/api/experiences?limit=50')
      const data = await response.json()
      const filteredExperiences = data.experiences.filter(exp => exp.lat && exp.lon).slice(0, 20)
      console.log('Fetched experiences:', filteredExperiences)
      setExperiences(filteredExperiences)
    } catch (error) {
      console.error('Error fetching experiences:', error)
    }
  }

  const handleAuth = (action) => {
    if (action === 'login') {
      window.location.href = '/login'
    } else {
      window.location.href = '/register'
    }
  }

  const logout = () => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    setIsLoggedIn(false)
  }

  // Initialize map when experiences are loaded
  useEffect(() => {
    if (experiences.length > 0 && mapContainer.current && !map.current) {
      initMap()
    }
  }, [experiences])

  // Create custom marker icon (from rawgle)
  const createCustomIcon = () => {
    return L.divIcon({
      html: '<div style="background: #FBBF24; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>',
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -15],
      className: 'custom-marker'
    })
  }

  // Initialize map (based on rawgle implementation)
  const initMap = () => {
    if (!mapContainer.current) return

    // Create map instance
    map.current = L.map(mapContainer.current).setView([34.08529, -117.9609], 10)
    
    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map.current)

    // Add markers for all experiences
    experiences.forEach(experience => {
      const marker = L.marker([experience.lat, experience.lon], {
        icon: createCustomIcon()
      }).addTo(map.current)
      
      // Create popup content
      const popupContent = `
        <div class="p-2 max-w-xs">
          <h3 class="font-bold text-sm text-gray-900 mb-1">${experience.title}</h3>
          <p class="text-indigo-600 font-medium text-xs mb-2">${experience.city_name}</p>
          <p class="text-gray-600 text-xs mb-3 leading-relaxed">
            ${experience.description?.slice(0, 120)}...
          </p>
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-1 text-yellow-500">
              ${'⭐'.repeat(5)}
              <span class="text-xs text-gray-500 ml-1">4.8</span>
            </div>
            <a href="/experience/${experience.id}" 
               class="bg-indigo-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-indigo-700 transition-colors">
              View
            </a>
          </div>
        </div>
      `
      
      marker.bindPopup(popupContent)
      markers.current.push(marker)
    })

    // Fit map to show all markers
    if (markers.current.length > 0) {
      const group = L.featureGroup(markers.current)
      map.current.fitBounds(group.getBounds().pad(0.1))
    }

    // Try to get user location
    getUserLocation()
  }

  // Get user location (from rawgle)
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLoc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
          setUserLocation(userLoc)
          
          // Add user location marker
          L.marker([userLoc.lat, userLoc.lng], {
            icon: L.divIcon({
              html: '<div style="background: #3B82F6; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"><div style="background: white; width: 8px; height: 8px; border-radius: 50%; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"></div></div>',
              iconSize: [20, 20],
              iconAnchor: [10, 10],
              className: 'user-location-marker'
            })
          }).addTo(map.current).bindPopup('You are here')
        },
        (error) => {
          console.log('Geolocation error:', error)
        }
      )
    }
  }

  // Cleanup map on unmount
  useEffect(() => {
    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-indigo-600">
                🗺️ Locrawl
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/explore" className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">
                Explore
              </Link>
              {isLoggedIn ? (
                <>
                  <Link to="/profile" className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">
                    Profile
                  </Link>
                  <button onClick={logout} className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-200">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => handleAuth('login')} className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">
                    Login
                  </button>
                  <button onClick={() => handleAuth('register')} className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700">
                    Sign Up
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Discover Hidden Local
              <span className="block text-yellow-400">Experiences</span>
            </h1>
            <p className="text-xl md:text-2xl text-indigo-100 mb-8 max-w-3xl mx-auto">
              From secret viewpoints to neighborhood gems, explore authentic places that locals love.
              Join our community of explorers and create your own adventure stories.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link
                to="/explore"
                className="inline-flex items-center bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-300 transition-colors duration-200 shadow-lg"
              >
                🗺️ Launch Interactive Map
              </Link>
              
              {!isLoggedIn && (
                <button
                  onClick={() => handleAuth('register')}
                  className="inline-flex items-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-indigo-600 transition-colors duration-200"
                >
                  ✨ Join Free
                </button>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto">
              <div>
                <div className="text-3xl font-bold text-yellow-400">{stats.cities.toLocaleString()}</div>
                <div className="text-indigo-200">Cities</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-yellow-400">{stats.experiences.toLocaleString()}</div>
                <div className="text-indigo-200">Experiences</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-yellow-400">24/7</div>
                <div className="text-indigo-200">Discovery</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-yellow-400">Free</div>
                <div className="text-indigo-200">Forever</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Map Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Explore the World Through Local Eyes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our interactive map shows you real experiences from real people. Click on any pin to discover something amazing.
            </p>
          </div>
          
          <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl relative" style={{ height: '500px' }}>
            {/* Leaflet map container */}
            <div 
              ref={mapContainer} 
              className="w-full h-full"
              style={{ borderRadius: '1rem' }}
            />
            
            {/* Overlay controls */}
            <div className="absolute top-4 left-4 right-4 flex justify-between items-start pointer-events-none">
              <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 flex items-center space-x-3 pointer-events-auto">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search experiences, cities..."
                  className="flex-1 bg-transparent border-none outline-none text-gray-700 placeholder-gray-400 w-64"
                />
                <button className="text-indigo-600 hover:text-indigo-700 font-medium text-sm">
                  Filter
                </button>
              </div>
            </div>
            
            {/* Stats overlay */}
            <div className="absolute bottom-4 left-4 pointer-events-none">
              <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3">
                <div className="text-sm text-gray-600">Showing</div>
                <div className="font-bold text-lg text-gray-900">{experiences.length} experiences</div>
                <div className="text-sm text-gray-500">in {stats.cities.toLocaleString()} cities</div>
                {experiences.length > 0 && (
                  <div className="text-xs text-green-600 mt-1">✓ Live data loaded</div>
                )}
              </div>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/explore"
              className="inline-flex items-center bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              View Full Map →
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Explore
            </h2>
            <p className="text-xl text-gray-600">
              From discovery to planning, we've got you covered
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">🗺️</div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Interactive Map</h3>
              <p className="text-gray-600">
                Explore experiences geographically with our beautiful interactive map powered by Mapbox.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">👤</div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Personal Profiles</h3>
              <p className="text-gray-600">
                Create your profile, track your adventures, and build your personal collection of discoveries.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">❤️</div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Favorites & Lists</h3>
              <p className="text-gray-600">
                Save your favorite spots and create custom lists for different trips and interests.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">⭐</div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Reviews & Ratings</h3>
              <p className="text-gray-600">
                Share your experiences and read authentic reviews from fellow travelers.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">📱</div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Mobile Ready</h3>
              <p className="text-gray-600">
                Access your discoveries anywhere with our responsive design that works on all devices.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">🌍</div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Global Community</h3>
              <p className="text-gray-600">
                Connect with locals and travelers worldwide who share your passion for authentic experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Discover by Category
            </h2>
            <p className="text-xl text-gray-600">
              Find exactly what you're looking for
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Link to="/explore?category=sunset" className="group">
              <div className="bg-gradient-to-br from-orange-400 to-red-500 p-8 rounded-xl text-white text-center hover:shadow-lg transition-all group-hover:scale-105">
                <div className="text-4xl mb-3">🌅</div>
                <h3 className="font-semibold">Sunset Spots</h3>
              </div>
            </Link>
            
            <Link to="/explore?category=food" className="group">
              <div className="bg-gradient-to-br from-green-400 to-emerald-500 p-8 rounded-xl text-white text-center hover:shadow-lg transition-all group-hover:scale-105">
                <div className="text-4xl mb-3">🍜</div>
                <h3 className="font-semibold">Local Eats</h3>
              </div>
            </Link>
            
            <Link to="/explore?category=photo" className="group">
              <div className="bg-gradient-to-br from-purple-400 to-indigo-500 p-8 rounded-xl text-white text-center hover:shadow-lg transition-all group-hover:scale-105">
                <div className="text-4xl mb-3">📸</div>
                <h3 className="font-semibold">Photo Spots</h3>
              </div>
            </Link>
            
            <Link to="/explore?category=nature" className="group">
              <div className="bg-gradient-to-br from-blue-400 to-cyan-500 p-8 rounded-xl text-white text-center hover:shadow-lg transition-all group-hover:scale-105">
                <div className="text-4xl mb-3">🌲</div>
                <h3 className="font-semibold">Nature</h3>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Exploring?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join thousands of explorers discovering authentic local experiences around the world.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/explore"
              className="inline-flex items-center bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-300 transition-colors"
            >
              🗺️ Start Exploring
            </Link>
            
            {!isLoggedIn && (
              <button
                onClick={() => handleAuth('register')}
                className="inline-flex items-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-indigo-600 transition-colors"
              >
                ✨ Create Free Account
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-2xl font-bold mb-4">🗺️ Locrawl</div>
            <p className="text-gray-400 mb-6">
              Discover authentic local experiences around the world
            </p>
            <div className="flex justify-center space-x-6 text-sm text-gray-400">
              <Link to="/about" className="hover:text-white">About</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}